import type { ReactNode } from 'react';

import { useLingui } from '@lingui/react/macro';
import { useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useAtomValue } from 'jotai';
import { useSelector } from 'react-redux';

import { patientInfoAtom } from '../modules/claims/freeClaims/state';
import { generateFileName } from '../utils/file';

import { makeAPIRequestReactQueryWithHeaders } from './api';
import {
  type ClaimSummaryGenerateRequest,
  buildClaimSummaryGenerateRequestBody,
} from './claimSummaryGenerate';
import { endpoints } from './endpoints';

export type GeneratePdfRequest = ClaimSummaryGenerateRequest;

interface GeneratePdfResponse {
  success: boolean;
  message: ReactNode;
}

/**
 * React Query mutation hook for generating and downloading PDFs
 *
 * @param options - Optional mutation options
 * @returns React Query mutation object
 */
export const useGeneratePdf = (
  options?: Parameters<
    typeof useMutation<GeneratePdfResponse, AxiosError, GeneratePdfRequest>
  >[0],
) => {
  const { t } = useLingui();
  const user = useSelector(
    (state: {
      login?: {
        user?: { primaryPracticeId?: string; primaryOrganizationId?: string };
      };
    }) => state?.login?.user,
  );
  const currentPracticeId = user?.primaryPracticeId;
  const currentOrganizationId = user?.primaryOrganizationId;
  const patientInfo = useAtomValue(patientInfoAtom);

  return useMutation({
    mutationFn: async (
      request: GeneratePdfRequest,
    ): Promise<GeneratePdfResponse> => {
      try {
        const requestBody = buildClaimSummaryGenerateRequestBody(request);

        const response = await makeAPIRequestReactQueryWithHeaders<Blob>(
          endpoints.pdfGenerate,
          undefined,
          {
            Practice: currentPracticeId,
            Org: currentOrganizationId,
          },
          'POST',
          {
            data: requestBody,
            responseType: 'blob',
          },
        );

        // Create a download link
        const url = window.URL.createObjectURL(response);
        const link = document.createElement('a');
        link.href = url;

        // Generate file name based on patient name if available, otherwise fall back to unified appointment ID
        // E.g. claim-summary-John-Doe-1234567890
        link.download = generateFileName(
          'claim-summary',
          request.unifiedAppointmentId,
          'pdf',
          patientInfo.name,
        );

        // Trigger the download
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Clean up the URL object
        window.URL.revokeObjectURL(url);

        return { success: true, message: t`PDF downloaded successfully` };
      } catch (error) {
        console.error('Error generating PDF:', error);
        throw new Error('Failed to generate PDF');
      }
    },
    ...options,
  });
};
